import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { formatAdminDate, getSupabaseErrorMessage } from "./adminConfig";

const BULK_CONCURRENCY = 8;

function parseNumberedVariantLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return { detected: false, canonicalName: raw, serialNumber: "", serialTotal: "" };

  // Example: "Blue Prizm 127/249" -> canonical "Blue Prizm /249", copy 127.
  const copyMatch = raw.match(/^(.*?)\s+(\d+)\s*\/\s*(\d+)$/);
  if (copyMatch) {
    const base = copyMatch[1].trim();
    const serialNumber = Number(copyMatch[2]);
    const serialTotal = Number(copyMatch[3]);
    if (base && serialNumber > 0 && serialTotal > 0 && serialNumber <= serialTotal) {
      return {
        detected: true,
        canonicalName: `${base} /${serialTotal}`,
        serialNumber: String(serialNumber),
        serialTotal: String(serialTotal),
      };
    }
  }

  // Example: "Red /5" -> already a canonical numbered-variant shape; copy is unknown.
  const totalMatch = raw.match(/^(.*?)\s*\/\s*(\d+)$/);
  if (totalMatch) {
    const base = totalMatch[1].trim();
    const serialTotal = Number(totalMatch[2]);
    if (base && serialTotal > 0) {
      return {
        detected: true,
        canonicalName: `${base} /${serialTotal}`,
        serialNumber: "",
        serialTotal: String(serialTotal),
      };
    }
  }

  return { detected: false, canonicalName: raw, serialNumber: "", serialTotal: "" };
}

function findSerialFromUserAttributes(attributes, expectedTotal) {
  if (!attributes || !expectedTotal) return "";
  const candidates = [
    attributes.parallel,
    attributes.variant,
    attributes.variation,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const parsed = parseNumberedVariantLabel(candidate);
    if (
      parsed.detected &&
      parsed.serialNumber &&
      Number(parsed.serialTotal) === Number(expectedTotal)
    ) {
      return parsed.serialNumber;
    }
  }
  return "";
}

function buildForm(suggestion) {
  const numbered = parseNumberedVariantLabel(suggestion?.name);
  const suggestionAttributes = suggestion?.attributes || {};
  const variantAttributes = { ...suggestionAttributes };
  if (numbered.serialTotal && variantAttributes.serial_total == null) {
    variantAttributes.serial_total = Number(numbered.serialTotal);
  }

  return {
    name: numbered.canonicalName || suggestion?.name || "",
    category: suggestion?.category || "",
    description: suggestion?.description || "",
    attributesText: JSON.stringify(variantAttributes, null, 2),
    serialNumber: numbered.serialNumber,
    serialTotal: numbered.serialTotal,
    numberedDetected: numbered.detected,
    originalName: suggestion?.name || "",
  };
}

function buildApplicabilityForm(context) {
  const subset = context?.card?.subset || "";
  return {
    scopeType: "item",
    subsetName: subset,
    groupId: "",
    createNewGroup: false,
    groupKey: "",
    groupName: "",
    sourceNote: "",
  };
}

function rowKey(value) {
  return String(value ?? "");
}

function isExactMatch(match) {
  return Boolean(
    match &&
      Number(match.candidate_count) === 1 &&
      match.variant_id
  );
}

function isSafeMatch(match) {
  return Boolean(isExactMatch(match) && match.is_applicable === true);
}

function needsApplicability(match) {
  return Boolean(isExactMatch(match) && match.is_applicable !== true);
}

function isNumberedSuggestion(suggestion) {
  return parseNumberedVariantLabel(suggestion?.name).detected;
}

function isBulkSafeSuggestion(suggestion, match) {
  return isSafeMatch(match) && !isNumberedSuggestion(suggestion);
}

function cardLabel(context) {
  const card = context?.card;
  if (!card) return "Unresolved canonical card";
  return [
    card.year,
    card.brand,
    card.set_name,
    card.subset,
    card.card_number ? `#${card.card_number}` : null,
    card.name,
  ]
    .filter(Boolean)
    .join(" · ");
}

function releaseLabel(context) {
  const release = context?.release;
  if (!release) return "—";
  return [release.year, release.brand, release.set_name || "Flagship/Base Release"]
    .filter(Boolean)
    .join(" · ");
}

async function runWithConcurrency(entries, worker, concurrency = BULK_CONCURRENCY) {
  const results = [];
  let cursor = 0;

  async function runner() {
    while (cursor < entries.length) {
      const index = cursor;
      cursor += 1;

      try {
        results[index] = {
          ok: true,
          value: await worker(entries[index], index),
        };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(concurrency, entries.length || 1) },
      () => runner()
    )
  );

  return results;
}

export default function VariantReviewManager({
  currentUser,
  onPendingCountChange,
  onResolveCard,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [form, setForm] = useState(buildForm(null));
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [applicability, setApplicability] = useState(buildApplicabilityForm(null));

  const [search, setSearch] = useState("");
  const [matchFilter, setMatchFilter] = useState("all");
  const [matchMap, setMatchMap] = useState({});
  const [unresolvedCardSuggestionIds, setUnresolvedCardSuggestionIds] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [loading, setLoading] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [matchWarning, setMatchWarning] = useState("");

  const pendingCount = suggestions.length;

  useEffect(() => {
    onPendingCountChange?.(pendingCount);
  }, [onPendingCountChange, pendingCount]);

  const fetchMatches = useCallback(async () => {
    setLoadingMatches(true);
    setMatchWarning("");

    const { data, error } = await supabase.rpc(
      "admin_pending_variant_matches_v2"
    );

    if (error) {
      console.warn("Unable to load applicability-aware variant matches:", error);
      setMatchMap({});
      setMatchWarning(
        "Applicability-aware exact matching is unavailable. Run 01_variant_write_path_upgrade.sql, then Refresh."
      );
      setLoadingMatches(false);
      return;
    }

    const next = {};
    (data || []).forEach((row) => {
      const key = rowKey(row.suggestion_id);
      const existing = next[key];

      if (!existing) {
        next[key] = row;
      } else if (
        Number(existing.candidate_count) !== 1 ||
        Number(row.candidate_count) !== 1 ||
        existing.variant_id !== row.variant_id
      ) {
        next[key] = {
          suggestion_id: key,
          variant_id: null,
          candidate_count: Math.max(
            Number(existing.candidate_count || 0),
            Number(row.candidate_count || 0),
            2
          ),
          is_applicable: false,
          applicability_ready: Boolean(
            existing.applicability_ready || row.applicability_ready
          ),
        };
      }
    });

    setMatchMap(next);
    setSelectedIds((current) => {
      const safeKeys = new Set(
        Object.entries(next)
          .filter(([, match]) => isSafeMatch(match))
          .map(([key]) => key)
      );
      return new Set([...current].filter((key) => safeKeys.has(rowKey(key))));
    });

    setLoadingMatches(false);
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const { data, error } = await supabase
      .from("user_variant_suggestions")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Unable to load variant suggestions:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to load variant review queue.")
      );
      setLoading(false);
      return;
    }

    const rows = data || [];

    // Canonical-card readiness is independent of whether the suggested variant
    // already has an exact canonical variant match. Resolve it for every queue row.
    const unresolvedSuggestionIds = new Set();
    const userItemIds = [...new Set(
      rows.map((row) => Number(row.user_item_id)).filter((id) => Number.isFinite(id) && id > 0)
    )];

    if (userItemIds.length > 0) {
      const { data: userItems, error: userItemsError } = await supabase
        .from("user_items")
        .select("id,item_id")
        .in("id", userItemIds);

      if (userItemsError) {
        console.warn("Unable to preflight canonical card links for Variant Review:", userItemsError);
      } else {
        const userItemMap = new Map((userItems || []).map((item) => [Number(item.id), item]));
        const canonicalItemIds = [...new Set(
          (userItems || [])
            .map((item) => Number(item.item_id))
            .filter((id) => Number.isFinite(id) && id > 0)
        )];

        let releaseReadyItemIds = new Set();
        if (canonicalItemIds.length > 0) {
          const { data: canonicalItems, error: canonicalItemsError } = await supabase
            .from("items")
            .select("id,release_id")
            .in("id", canonicalItemIds);

          if (canonicalItemsError) {
            console.warn("Unable to preflight canonical releases for Variant Review:", canonicalItemsError);
          } else {
            releaseReadyItemIds = new Set(
              (canonicalItems || [])
                .filter((item) => item.release_id != null)
                .map((item) => Number(item.id))
            );
          }
        }

        rows.forEach((row) => {
          const userItem = userItemMap.get(Number(row.user_item_id));
          const canonicalItemId = Number(userItem?.item_id);
          if (
            !userItem ||
            !Number.isFinite(canonicalItemId) ||
            canonicalItemId <= 0 ||
            !releaseReadyItemIds.has(canonicalItemId)
          ) {
            unresolvedSuggestionIds.add(rowKey(row.id));
          }
        });
      }
    }

    // Suggestions without a user item are unresolved by definition.
    rows.forEach((row) => {
      if (!row.user_item_id) unresolvedSuggestionIds.add(rowKey(row.id));
    });
    setUnresolvedCardSuggestionIds(unresolvedSuggestionIds);

    setSuggestions(rows);
    setSelectedSuggestion((current) => {
      if (!current) return null;
      return rows.find((row) => rowKey(row.id) === rowKey(current.id)) || null;
    });

    setSelectedIds((current) => {
      const live = new Set(rows.map((row) => rowKey(row.id)));
      return new Set([...current].filter((key) => live.has(rowKey(key))));
    });

    setLoading(false);
    await fetchMatches();
  }, [fetchMatches]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const loadSuggestionContext = useCallback(async (suggestion) => {
    if (!suggestion) return null;

    const match = matchMap[rowKey(suggestion.id)];
    const variantId = isExactMatch(match) ? Number(match.variant_id) : null;

    setContextLoading(true);
    const { data, error } = await supabase.rpc(
      "admin_variant_suggestion_context",
      {
        p_suggestion_id: suggestion.id,
        p_variant_id: variantId,
      }
    );
    setContextLoading(false);

    if (error) throw error;

    let enrichedContext = data || null;
    if (suggestion.user_item_id) {
      const { data: userItem, error: userItemError } = await supabase
        .from("user_items")
        .select("id,attributes")
        .eq("id", suggestion.user_item_id)
        .maybeSingle();

      if (userItemError) {
        console.warn("Unable to load user-item serial evidence:", userItemError);
      } else if (userItem) {
        enrichedContext = { ...(data || {}), user_item: userItem };
        setForm((prev) => {
          if (!prev.numberedDetected || prev.serialNumber) return prev;
          const recovered = findSerialFromUserAttributes(
            userItem.attributes || {},
            prev.serialTotal
          );
          return recovered ? { ...prev, serialNumber: recovered } : prev;
        });
      }
    }

    setContext(enrichedContext);
    setApplicability(buildApplicabilityForm(enrichedContext));
    return enrichedContext;
  }, [matchMap]);

  const openSuggestion = async (suggestion) => {
    setSelectedSuggestion(suggestion);
    setForm(buildForm(suggestion));
    setContext(null);
    setApplicability(buildApplicabilityForm(null));
    setErrorMessage("");
    setStatusMessage("");

    try {
      await loadSuggestionContext(suggestion);
    } catch (error) {
      console.error("Unable to load variant applicability context:", error);
      setErrorMessage(
        getSupabaseErrorMessage(
          error,
          "Unable to load the canonical card/release context for this suggestion."
        )
      );
    }
  };

  const buildApplicabilityArgs = () => {
    const match = selectedSuggestion
      ? matchMap[rowKey(selectedSuggestion.id)]
      : null;

    if (isSafeMatch(match)) {
      return {
        p_scope_type: null,
        p_subset_name: null,
        p_group_id: null,
        p_group_key: null,
        p_group_name: null,
      };
    }

    if (!context?.applicability_ready) {
      throw new Error(
        "Resolve this user item to a canonical card before approving its variant applicability."
      );
    }

    const scopeType = applicability.scopeType;
    if (!["item", "subset", "group", "release"].includes(scopeType)) {
      throw new Error("Choose where this variant applies.");
    }

    if (scopeType === "subset" && !String(applicability.subsetName || "").trim()) {
      throw new Error("Subset scope requires a subset name.");
    }

    if (scopeType === "group") {
      if (applicability.createNewGroup) {
        if (!applicability.groupKey.trim() || !applicability.groupName.trim()) {
          throw new Error("A new group requires both a group key and group name.");
        }
      } else if (!applicability.groupId) {
        throw new Error("Choose an existing group or create a new one.");
      }
    }

    return {
      p_scope_type: scopeType,
      p_subset_name:
        scopeType === "subset" ? applicability.subsetName.trim() : null,
      p_group_id:
        scopeType === "group" && !applicability.createNewGroup
          ? Number(applicability.groupId)
          : null,
      p_group_key:
        scopeType === "group" && applicability.createNewGroup
          ? applicability.groupKey.trim()
          : null,
      p_group_name:
        scopeType === "group" && applicability.createNewGroup
          ? applicability.groupName.trim()
          : null,
    };
  };

  const resolveSuggestion = useCallback(
    async ({ suggestion, match, createPayload = null, applicabilityArgs = {}, sourceNote = "" }) => {
      const exact = isExactMatch(match);
      const { data, error } = await supabase.rpc(
        "admin_resolve_variant_suggestion_with_serial",
        {
          p_suggestion_id: suggestion.id,
          p_variant_id: exact ? Number(match.variant_id) : null,
          p_name: createPayload?.name || null,
          p_category: createPayload?.category || null,
          p_description: createPayload?.description || null,
          p_attributes: createPayload?.attributes || {},
          p_serial_number: createPayload?.serialNumber ?? null,
          p_serial_total: createPayload?.serialTotal ?? null,
          p_scope_type: applicabilityArgs.p_scope_type ?? null,
          p_subset_name: applicabilityArgs.p_subset_name ?? null,
          p_group_id: applicabilityArgs.p_group_id ?? null,
          p_group_key: applicabilityArgs.p_group_key ?? null,
          p_group_name: applicabilityArgs.p_group_name ?? null,
          p_source_note:
            String(sourceNote || "").trim() ||
            `Approved in Variant Review from suggestion #${suggestion.id}`,
        }
      );

      if (error) throw error;
      return data;
    },
    []
  );

  const quickLinkExisting = async (suggestion) => {
    const match = matchMap[rowKey(suggestion.id)];
    if (!isSafeMatch(match)) return;

    if (
      !window.confirm(
        `Approve ${suggestion.name || `suggestion #${suggestion.id}`} by linking it to applicable variant ${match.variant_name}?`
      )
    ) return;

    setBulkBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      await resolveSuggestion({
        suggestion,
        match,
        applicabilityArgs: {
          p_scope_type: null,
          p_subset_name: null,
          p_group_id: null,
          p_group_key: null,
          p_group_name: null,
        },
      });

      const key = rowKey(suggestion.id);
      setSuggestions((prev) => prev.filter((row) => rowKey(row.id) !== key));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      if (rowKey(selectedSuggestion?.id) === key) setSelectedSuggestion(null);

      setStatusMessage(
        `Linked ${suggestion.name || "variant suggestion"} to applicable variant ${match.variant_name}.`
      );
      await fetchMatches();
    } catch (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to link the variant suggestion.")
      );
    }

    setBulkBusy(false);
  };

  const approveSuggestion = async () => {
    if (!selectedSuggestion) return;
    if (!currentUser?.id) {
      setErrorMessage("The current admin user ID is missing.");
      return;
    }

    const match = matchMap[rowKey(selectedSuggestion.id)];
    const exact = isExactMatch(match);

    let attributes = {};
    let createPayload = null;

    if (!exact) {
      const name = form.name.trim();
      if (!name) {
        setErrorMessage("Variant name is required.");
        return;
      }

      try {
        attributes = form.attributesText.trim()
          ? JSON.parse(form.attributesText)
          : {};
      } catch (error) {
        setErrorMessage(`Attributes must be valid JSON: ${error.message}`);
        return;
      }

      const serialNumber = form.serialNumber ? Number(form.serialNumber) : null;
      const serialTotal = form.serialTotal ? Number(form.serialTotal) : null;

      if (serialNumber != null && (!Number.isInteger(serialNumber) || serialNumber <= 0)) {
        setErrorMessage("Serial number must be a positive whole number.");
        return;
      }
      if (serialTotal != null && (!Number.isInteger(serialTotal) || serialTotal <= 0)) {
        setErrorMessage("Numbered-to value must be a positive whole number.");
        return;
      }
      if (serialNumber != null && serialTotal == null) {
        setErrorMessage("A serial number requires a numbered-to value.");
        return;
      }
      if (serialNumber != null && serialTotal != null && serialNumber > serialTotal) {
        setErrorMessage("Serial number cannot be greater than the numbered-to value.");
        return;
      }

      if (serialTotal != null) {
        attributes = { ...attributes, serial_total: serialTotal };
      }

      createPayload = {
        name,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        attributes,
        serialNumber,
        serialTotal,
      };
    } else if (form.numberedDetected) {
      const serialNumber = form.serialNumber ? Number(form.serialNumber) : null;
      const serialTotal = form.serialTotal ? Number(form.serialTotal) : null;
      if (serialNumber != null && (!Number.isInteger(serialNumber) || serialNumber <= 0)) {
        setErrorMessage("Serial number must be a positive whole number.");
        return;
      }
      if (serialTotal == null || !Number.isInteger(serialTotal) || serialTotal <= 0) {
        setErrorMessage("Numbered-to value must be a positive whole number.");
        return;
      }
      if (serialNumber != null && serialNumber > serialTotal) {
        setErrorMessage("Serial number cannot be greater than the numbered-to value.");
        return;
      }
      createPayload = {
        attributes: {},
        serialNumber,
        serialTotal,
      };
    }

    let applicabilityArgs;
    try {
      applicabilityArgs = buildApplicabilityArgs();
    } catch (error) {
      setErrorMessage(error.message);
      return;
    }

    const scopeLabel = isSafeMatch(match)
      ? "existing verified applicability"
      : applicability.scopeType;
    const variantLabel = exact ? match.variant_name : createPayload.name;

    if (
      !window.confirm(
        `Approve ${variantLabel} using ${scopeLabel} applicability?`
      )
    ) return;

    setBusyId(selectedSuggestion.id);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const result = await resolveSuggestion({
        suggestion: selectedSuggestion,
        match,
        createPayload,
        applicabilityArgs,
        sourceNote: applicability.sourceNote,
      });

      setStatusMessage(
        `Approved ${result?.variant_name || variantLabel}${
          result?.applicability_scope
            ? ` with ${result.applicability_scope} applicability`
            : " using existing applicability"
        }.`
      );
      setSelectedSuggestion(null);
      setContext(null);
      await fetchSuggestions();
    } catch (error) {
      console.error("Unable to resolve variant suggestion:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to approve variant suggestion.")
      );
    }

    setBusyId(null);
  };

  const denySuggestion = async (suggestion, { confirm = true } = {}) => {
    if (
      confirm &&
      !window.confirm(
        `Deny variant suggestion ${suggestion.name || `#${suggestion.id}`}? It will remain in history as denied.`
      )
    ) return { cancelled: true };

    const { error } = await supabase.rpc("admin_review_deny_variant_suggestion", {
      p_suggestion_id: suggestion.id,
    });

    if (error) throw error;
    return { ok: true };
  };

  const handleDenySuggestion = async (suggestion) => {
    setBusyId(suggestion.id);
    setErrorMessage("");
    setStatusMessage("");

    try {
      const result = await denySuggestion(suggestion);
      if (result?.cancelled) {
        setBusyId(null);
        return;
      }

      const key = rowKey(suggestion.id);
      setSuggestions((prev) => prev.filter((row) => rowKey(row.id) !== key));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      if (rowKey(selectedSuggestion?.id) === key) {
        setSelectedSuggestion(null);
        setContext(null);
      }

      setStatusMessage(
        `Denied variant suggestion ${suggestion.name || `#${suggestion.id}`}.`
      );
      await fetchMatches();
    } catch (error) {
      console.error("Unable to deny variant suggestion:", error);
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to deny variant suggestion.")
      );
    }

    setBusyId(null);
  };

  const filteredSuggestions = useMemo(() => {
    const term = search.trim().toLowerCase();

    return suggestions.filter((suggestion) => {
      const match = matchMap[rowKey(suggestion.id)];
      const safe = isSafeMatch(match);
      const exactNeedsScope = needsApplicability(match);
      const ambiguous = Number(match?.candidate_count || 0) > 1;
      const hasExact = isExactMatch(match);

      if (matchFilter === "safe" && !safe) return false;
      if (matchFilter === "needs_scope" && !exactNeedsScope) return false;
      if (matchFilter === "new" && (hasExact || ambiguous)) return false;
      if (matchFilter === "ambiguous" && !ambiguous) return false;

      if (!term) return true;

      return [
        suggestion.name,
        suggestion.category,
        suggestion.description,
        JSON.stringify(suggestion.attributes || {}),
        match?.variant_name,
        match?.variant_category,
        match?.canonical_subset,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [search, suggestions, matchFilter, matchMap]);

  const safeTotal = useMemo(
    () => suggestions.filter((s) => isBulkSafeSuggestion(s, matchMap[rowKey(s.id)])).length,
    [suggestions, matchMap]
  );

  const needsScopeTotal = useMemo(
    () => suggestions.filter((s) => needsApplicability(matchMap[rowKey(s.id)])).length,
    [suggestions, matchMap]
  );

  const selectedSuggestions = useMemo(
    () => suggestions.filter((s) => selectedIds.has(rowKey(s.id))),
    [suggestions, selectedIds]
  );

  const selectedSafeSuggestions = useMemo(
    () => selectedSuggestions.filter((s) => isBulkSafeSuggestion(s, matchMap[rowKey(s.id)])),
    [selectedSuggestions, matchMap]
  );

  const visibleSafeSuggestions = useMemo(
    () => filteredSuggestions.filter((s) => isBulkSafeSuggestion(s, matchMap[rowKey(s.id)])),
    [filteredSuggestions, matchMap]
  );

  const allVisibleSafeSelected =
    visibleSafeSuggestions.length > 0 &&
    visibleSafeSuggestions.every((s) => selectedIds.has(rowKey(s.id)));

  const toggleSelected = (suggestion) => {
    const key = rowKey(suggestion.id);
    if (!isBulkSafeSuggestion(suggestion, matchMap[key])) return;

    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisibleSafe = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSafeSelected) {
        visibleSafeSuggestions.forEach((s) => next.delete(rowKey(s.id)));
      } else {
        visibleSafeSuggestions.forEach((s) => next.add(rowKey(s.id)));
      }
      return next;
    });
  };

  const selectAllSafe = () => {
    setSelectedIds(
      new Set(
        suggestions
          .filter((s) => isBulkSafeSuggestion(s, matchMap[rowKey(s.id)]))
          .map((s) => rowKey(s.id))
      )
    );
    // Match Item Review behavior: after selecting every safe row,
    // narrow the queue to the selected safe set so the admin can review
    // exactly what will be bulk-linked.
    setMatchFilter("safe");
  };

  const bulkLinkSafe = async () => {
    if (!selectedSafeSuggestions.length) return;

    if (
      !window.confirm(
        `Link ${selectedSafeSuggestions.length} selected suggestion${
          selectedSafeSuggestions.length === 1 ? "" : "s"
        } to exact variants that are already applicable?`
      )
    ) return;

    setBulkBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const results = await runWithConcurrency(
      selectedSafeSuggestions,
      async (suggestion) => {
        const match = matchMap[rowKey(suggestion.id)];
        const result = await resolveSuggestion({
          suggestion,
          match,
          applicabilityArgs: {
            p_scope_type: null,
            p_subset_name: null,
            p_group_id: null,
            p_group_key: null,
            p_group_name: null,
          },
        });
        return rowKey(result?.suggestion_id || suggestion.id);
      }
    );

    const successful = new Set();
    const failures = [];
    results.forEach((result, index) => {
      if (result?.ok) successful.add(rowKey(result.value));
      else failures.push({ suggestion: selectedSafeSuggestions[index], error: result?.error });
    });

    setSuggestions((prev) =>
      prev.filter((suggestion) => !successful.has(rowKey(suggestion.id)))
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      successful.forEach((id) => next.delete(id));
      return next;
    });

    if (failures.length) {
      setErrorMessage(
        `${successful.size} safe suggestion${successful.size === 1 ? "" : "s"} linked; ${
          failures.length
        } failed. First error: ${getSupabaseErrorMessage(
          failures[0].error,
          "Unknown bulk-link error."
        )}`
      );
    } else {
      setStatusMessage(
        `Linked ${successful.size} suggestion${successful.size === 1 ? "" : "s"} to already-applicable variants.`
      );
    }

    await fetchMatches();
    setBulkBusy(false);
  };

  const bulkDenySelected = async () => {
    if (!selectedSuggestions.length) return;

    if (
      !window.confirm(
        `Deny ${selectedSuggestions.length} selected variant suggestion${
          selectedSuggestions.length === 1 ? "" : "s"
        }?`
      )
    ) return;

    setBulkBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const results = await runWithConcurrency(
      selectedSuggestions,
      async (suggestion) => {
        await denySuggestion(suggestion, { confirm: false });
        return rowKey(suggestion.id);
      }
    );

    const successful = new Set();
    const failures = [];
    results.forEach((result, index) => {
      if (result?.ok) successful.add(rowKey(result.value));
      else failures.push({ suggestion: selectedSuggestions[index], error: result?.error });
    });

    setSuggestions((prev) =>
      prev.filter((suggestion) => !successful.has(rowKey(suggestion.id)))
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      successful.forEach((id) => next.delete(id));
      return next;
    });

    if (selectedSuggestion && successful.has(rowKey(selectedSuggestion.id))) {
      setSelectedSuggestion(null);
      setContext(null);
    }

    if (failures.length) {
      setErrorMessage(
        `${successful.size} suggestion${successful.size === 1 ? "" : "s"} denied; ${
          failures.length
        } failed. First error: ${getSupabaseErrorMessage(
          failures[0].error,
          "Unknown bulk-deny error."
        )}`
      );
    } else {
      setStatusMessage(
        `Denied ${successful.size} suggestion${successful.size === 1 ? "" : "s"}.`
      );
    }

    await fetchMatches();
    setBulkBusy(false);
  };

  const selectedMatch = selectedSuggestion
    ? matchMap[rowKey(selectedSuggestion.id)]
    : null;
  const selectedExact = isExactMatch(selectedMatch);
  const selectedSafe = isBulkSafeSuggestion(selectedSuggestion, selectedMatch);
  const groups = Array.isArray(context?.groups) ? context.groups : [];

  return (
    <div>
      <div
        className="hadmin-toolbar"
        style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}
      >
        <input
          className="hadmin-input hadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search pending variant suggestions..."
        />

        <select
          className="hadmin-input"
          value={matchFilter}
          onChange={(event) => setMatchFilter(event.target.value)}
          style={{ minWidth: 220 }}
        >
          <option value="all">All pending</option>
          <option value="safe">Safe exact + applicable</option>
          <option value="needs_scope">Exact variant, needs applicability</option>
          <option value="new">Likely new variant</option>
          <option value="ambiguous">Ambiguous variant match</option>
        </select>

        <button
          type="button"
          className="hadmin-btn-secondary"
          onClick={fetchSuggestions}
          disabled={loading || bulkBusy}
        >
          {loading || loadingMatches ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="hadmin-info">
        <strong>{safeTotal}</strong> suggestion{safeTotal === 1 ? "" : "s"} match one
        canonical variant that is already applicable and can be linked safely. {" "}
        <strong>{needsScopeTotal}</strong> exact match{needsScopeTotal === 1 ? "" : "es"} need
        an applicability decision before approval.
      </div>

      {matchWarning && <div className="hadmin-error">{matchWarning}</div>}
      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}
      {statusMessage && <div className="hadmin-success">{statusMessage}</div>}

      <section className="hadmin-panel">
        <div
          className="hadmin-row-between"
          style={{ marginBottom: 14, gap: 12, flexWrap: "wrap" }}
        >
          <div>
            <strong>
              {suggestions.length} pending variant suggestion
              {suggestions.length === 1 ? "" : "s"}
            </strong>
            <div className="hadmin-muted" style={{ marginTop: 3, fontSize: 12 }}>
              {filteredSuggestions.length} shown · {selectedSuggestions.length} selected
            </div>
          </div>

          <div className="hadmin-actions" style={{ flexWrap: "wrap" }}>
            <button
              type="button"
              className="hadmin-btn-secondary"
              disabled={!safeTotal || bulkBusy}
              onClick={selectAllSafe}
            >
              Select All {safeTotal} Safe
            </button>

            <button
              type="button"
              className="hadmin-btn-success"
              disabled={!selectedSafeSuggestions.length || bulkBusy}
              onClick={bulkLinkSafe}
            >
              {bulkBusy ? "Working..." : `Link ${selectedSafeSuggestions.length || ""} Safe`}
            </button>

            <button
              type="button"
              className="hadmin-btn-danger"
              disabled={!selectedSuggestions.length || bulkBusy}
              onClick={bulkDenySelected}
            >
              Deny {selectedSuggestions.length || ""} Selected
            </button>
          </div>
        </div>

        {loading ? (
          <div className="hadmin-empty">Loading variant suggestions...</div>
        ) : !filteredSuggestions.length ? (
          <div className="hadmin-empty">
            No pending variant suggestions match the current filter.
          </div>
        ) : (
          <div className="hadmin-table-wrap">
            <table className="hadmin-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSafeSelected}
                      onChange={toggleAllVisibleSafe}
                      disabled={!visibleSafeSuggestions.length || bulkBusy}
                      title="Select visible safe exact/applicable matches"
                    />
                  </th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Canonical Match</th>
                  <th>Applicability</th>
                  <th>Suggested</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredSuggestions.map((suggestion) => {
                  const key = rowKey(suggestion.id);
                  const match = matchMap[key];
                  const exactApplicable = isSafeMatch(match);
                  const safe = isBulkSafeSuggestion(suggestion, match);
                  const exactNeedsScope = needsApplicability(match);
                  const cardUnresolved = unresolvedCardSuggestionIds.has(
                    rowKey(suggestion.id)
                  );
                  const ambiguous = Number(match?.candidate_count || 0) > 1;

                  return (
                    <tr
                      key={suggestion.id}
                      className={selectedSuggestion?.id === suggestion.id ? "selected" : ""}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(key)}
                          onChange={() => toggleSelected(suggestion)}
                          disabled={bulkBusy || !safe}
                          title={safe ? "Select safe link" : "Manual review required"}
                        />
                      </td>

                      <td>
                        <strong>{suggestion.name || "Unnamed variant"}</strong>
                        <div className="hadmin-muted">#{suggestion.id}</div>
                      </td>

                      <td>{suggestion.category || "—"}</td>

                      <td>
                        {exactApplicable || exactNeedsScope ? (
                          <div>
                            <strong>{match.variant_name}</strong>
                            <div className="hadmin-muted" style={{ marginTop: 3 }}>
                              {match.variant_category || "No category"} · variant #{match.variant_id}
                            </div>
                          </div>
                        ) : ambiguous ? (
                          <div>
                            <strong>{match.candidate_count} matching variants</strong>
                            <div className="hadmin-muted">Manual review required.</div>
                          </div>
                        ) : (
                          <span className="hadmin-muted">No exact existing variant</span>
                        )}
                      </td>

                      <td>
                        {cardUnresolved ? (
                          <div>
                            <span className="hadmin-pill">Card unresolved</span>
                            <div className="hadmin-muted" style={{ marginTop: 4 }}>
                              Resolve canonical link first.
                            </div>
                          </div>
                        ) : exactApplicable ? (
                          <span className="hadmin-pill green">Verified for card</span>
                        ) : exactNeedsScope ? (
                          <span className="hadmin-pill">Needs scope</span>
                        ) : (
                          <span className="hadmin-muted">Review</span>
                        )}
                      </td>

                      <td>{formatAdminDate(suggestion.created_at)}</td>

                      <td>
                        <div className="hadmin-actions" style={{ flexWrap: "wrap" }}>
                          {safe && (
                            <button
                              type="button"
                              className="hadmin-btn-success"
                              disabled={bulkBusy}
                              onClick={() => quickLinkExisting(suggestion)}
                            >
                              Link Existing
                            </button>
                          )}

                          <div style={{ width: 104, flex: "0 0 104px" }}>
                            {cardUnresolved && suggestion.user_item_id ? (
                              <button
                                type="button"
                                className="hadmin-btn-secondary"
                                style={{ width: "100%" }}
                                onClick={() => onResolveCard?.(suggestion.user_item_id)}
                                disabled={bulkBusy}
                                title={`Open user item #${suggestion.user_item_id} in Item Review`}
                              >
                                Resolve Card
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="hadmin-btn-secondary"
                                style={{ width: "100%" }}
                                onClick={() => openSuggestion(suggestion)}
                                disabled={bulkBusy}
                              >
                                Review
                              </button>
                            )}
                          </div>

                          <button
                            type="button"
                            className="hadmin-btn-danger"
                            disabled={busyId === suggestion.id || bulkBusy}
                            onClick={() => handleDenySuggestion(suggestion)}
                          >
                            Deny
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedSuggestion && (
        <>
          <div
            className="hadmin-drawer-backdrop"
            onClick={() => !busyId && setSelectedSuggestion(null)}
          />

          <aside className="hadmin-drawer">
            <div className="hadmin-drawer-header">
              <div>
                <h2>{selectedSuggestion.name || "Variant Suggestion"}</h2>
                <p>Suggestion #{selectedSuggestion.id}</p>
              </div>

              <button
                type="button"
                className="hadmin-icon-btn"
                disabled={Boolean(busyId)}
                onClick={() => setSelectedSuggestion(null)}
              >
                ×
              </button>
            </div>

            <div className="hadmin-drawer-body">
              {selectedSafe && (
                <div className="hadmin-success" style={{ marginBottom: 18 }}>
                  Exact canonical variant <strong>{selectedMatch.variant_name}</strong> already
                  applies to this card. This suggestion can be linked without creating a new rule.
                </div>
              )}

              {needsApplicability(selectedMatch) && (
                <div className="hadmin-info" style={{ marginBottom: 18 }}>
                  Exact canonical variant <strong>{selectedMatch.variant_name}</strong> exists,
                  but Hobbli does not yet have a rule making it applicable to this card. Choose the
                  correct scope below.
                </div>
              )}

              {contextLoading ? (
                <div className="hadmin-empty">Loading canonical card context...</div>
              ) : context?.applicability_ready ? (
                <div className="hadmin-card" style={{ marginBottom: 18 }}>
                  <strong>Canonical Card</strong>
                  <div style={{ marginTop: 6 }}>{cardLabel(context)}</div>
                  <div className="hadmin-muted" style={{ marginTop: 5, fontSize: 12 }}>
                    Release #{context?.release?.id}: {releaseLabel(context)}
                  </div>
                </div>
              ) : (
                <div className="hadmin-error" style={{ marginBottom: 18 }}>
                  <div>
                    This user item is not linked to a canonical card/release yet. Resolve it in Item
                    Review before approving this variant suggestion. The user's custom label remains
                    preserved while it waits.
                  </div>
                  {selectedSuggestion?.user_item_id && onResolveCard && (
                    <button
                      type="button"
                      className="hadmin-btn-secondary"
                      style={{ marginTop: 12 }}
                      onClick={() => onResolveCard(selectedSuggestion.user_item_id)}
                    >
                      Resolve Card
                    </button>
                  )}
                </div>
              )}

              {selectedExact && form.numberedDetected && (
                <div className="hadmin-form-grid">
                  <div className="hadmin-form-field full">
                    <div className="hadmin-info" style={{ marginBottom: 8 }}>
                      Numbered-card evidence detected from <strong>{form.originalName}</strong>.
                      This will link to <strong>{selectedMatch?.variant_name}</strong>; review the copy serial before approval.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <label>Copy Serial #</label>
                        <input
                          className="hadmin-input"
                          inputMode="numeric"
                          value={form.serialNumber}
                          placeholder="e.g. 3 (optional)"
                          onChange={(e) => setForm((prev) => ({ ...prev, serialNumber: e.target.value.replace(/[^0-9]/g, "") }))}
                        />
                      </div>
                      <div>
                        <label>Numbered To</label>
                        <input
                          className="hadmin-input"
                          inputMode="numeric"
                          value={form.serialTotal}
                          placeholder="e.g. 5"
                          onChange={(e) => setForm((prev) => ({ ...prev, serialTotal: e.target.value.replace(/[^0-9]/g, "") }))}
                        />
                      </div>
                    </div>
                    <div className="hadmin-muted" style={{ marginTop: 6, fontSize: 11 }}>
                      Copy Serial # is stored only on this user's item. Numbered To is canonical variant metadata.
                    </div>
                  </div>
                </div>
              )}

              {!selectedExact && (
                <div className="hadmin-form-grid">
                  <div className="hadmin-form-field full">
                    <label>Name</label>
                    <input
                      className="hadmin-input"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>

                  {form.numberedDetected && (
                    <div className="hadmin-form-field full">
                      <div className="hadmin-info" style={{ marginBottom: 8 }}>
                        Numbered-card evidence detected from <strong>{form.originalName}</strong>.
                        Review the reusable variant name and copy serial before approval.
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <label>Copy Serial #</label>
                          <input
                            className="hadmin-input"
                            inputMode="numeric"
                            value={form.serialNumber}
                            placeholder="e.g. 127 (optional)"
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                serialNumber: e.target.value.replace(/[^0-9]/g, ""),
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label>Numbered To</label>
                          <input
                            className="hadmin-input"
                            inputMode="numeric"
                            value={form.serialTotal}
                            placeholder="e.g. 249"
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                serialTotal: e.target.value.replace(/[^0-9]/g, ""),
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="hadmin-muted" style={{ marginTop: 6, fontSize: 11 }}>
                        Copy Serial # is stored only on this user's item. Numbered To is canonical variant metadata.
                      </div>
                    </div>
                  )}

                  <div className="hadmin-form-field full">
                    <label>Category</label>
                    <input
                      className="hadmin-input"
                      value={form.category}
                      onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                    />
                  </div>

                  <div className="hadmin-form-field full">
                    <label>Description</label>
                    <textarea
                      className="hadmin-textarea"
                      value={form.description}
                      onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </div>

                  <div className="hadmin-form-field full">
                    <label>Attributes (JSON)</label>
                    <textarea
                      className="hadmin-textarea hadmin-code-input"
                      value={form.attributesText}
                      onChange={(e) => setForm((prev) => ({ ...prev, attributesText: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {!isSafeMatch(selectedMatch) && context?.applicability_ready && (
                <>
                  <h3 style={{ marginTop: 22 }}>Applicability</h3>
                  <div className="hadmin-info" style={{ marginBottom: 12 }}>
                    Approval creates an <strong>INCLUDE</strong> rule. Choose the narrowest verified
                    scope supported by the checklist/odds evidence.
                  </div>

                  <div className="hadmin-form-grid">
                    <div className="hadmin-form-field full">
                      <label>Applies to</label>
                      <select
                        className="hadmin-input"
                        value={applicability.scopeType}
                        onChange={(e) =>
                          setApplicability((prev) => ({
                            ...prev,
                            scopeType: e.target.value,
                            createNewGroup: false,
                          }))
                        }
                      >
                        <option value="item">This card only</option>
                        <option value="subset" disabled={!context?.card?.subset}>
                          Entire subset{context?.card?.subset ? ` — ${context.card.subset}` : ""}
                        </option>
                        <option value="group">Defined item group</option>
                        <option value="release">Entire release</option>
                      </select>
                    </div>

                    {applicability.scopeType === "subset" && (
                      <div className="hadmin-form-field full">
                        <label>Subset</label>
                        <input
                          className="hadmin-input"
                          value={applicability.subsetName}
                          onChange={(e) =>
                            setApplicability((prev) => ({ ...prev, subsetName: e.target.value }))
                          }
                        />
                      </div>
                    )}

                    {applicability.scopeType === "group" && (
                      <>
                        <div className="hadmin-form-field full">
                          <label>Group</label>
                          <select
                            className="hadmin-input"
                            value={applicability.createNewGroup ? "__new__" : applicability.groupId}
                            onChange={(e) =>
                              setApplicability((prev) => ({
                                ...prev,
                                createNewGroup: e.target.value === "__new__",
                                groupId: e.target.value === "__new__" ? "" : e.target.value,
                              }))
                            }
                          >
                            <option value="">Choose group</option>
                            {groups.map((group) => (
                              <option key={group.id} value={group.id} disabled={!group.contains_item}>
                                {group.name} ({group.group_key}){group.contains_item ? " · contains this card" : " · card not in group"}
                              </option>
                            ))}
                            <option value="__new__">+ Create new group</option>
                          </select>
                        </div>

                        {applicability.createNewGroup && (
                          <>
                            <div className="hadmin-form-field full">
                              <label>New Group Key</label>
                              <input
                                className="hadmin-input"
                                value={applicability.groupKey}
                                placeholder="e.g. player-number-variation-subjects"
                                onChange={(e) =>
                                  setApplicability((prev) => ({ ...prev, groupKey: e.target.value }))
                                }
                              />
                            </div>
                            <div className="hadmin-form-field full">
                              <label>New Group Name</label>
                              <input
                                className="hadmin-input"
                                value={applicability.groupName}
                                placeholder="e.g. Player Number Variation Subjects"
                                onChange={(e) =>
                                  setApplicability((prev) => ({ ...prev, groupName: e.target.value }))
                                }
                              />
                              <div className="hadmin-muted" style={{ marginTop: 5, fontSize: 11 }}>
                                The new group starts with this reviewed card as its first member. Expand
                                membership later when the verified subject list is available.
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div className="hadmin-form-field full">
                      <label>Source / Review Note</label>
                      <input
                        className="hadmin-input"
                        value={applicability.sourceNote}
                        placeholder="Optional evidence or admin note"
                        onChange={(e) =>
                          setApplicability((prev) => ({ ...prev, sourceNote: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              <h3 style={{ marginTop: 22 }}>Suggestion Record</h3>
              <pre className="hadmin-json">
                {JSON.stringify(selectedSuggestion, null, 2)}
              </pre>
            </div>

            <div className="hadmin-drawer-footer">
              <button
                type="button"
                className="hadmin-btn-danger"
                disabled={Boolean(busyId)}
                onClick={() => handleDenySuggestion(selectedSuggestion)}
              >
                Deny
              </button>

              {selectedSafe && (
                <button
                  type="button"
                  className="hadmin-btn-success"
                  disabled={Boolean(busyId) || bulkBusy}
                  onClick={() => quickLinkExisting(selectedSuggestion)}
                >
                  Link Existing Variant
                </button>
              )}

              <button
                type="button"
                className="hadmin-btn-success"
                disabled={
                  Boolean(busyId) ||
                  contextLoading ||
                  !context?.applicability_ready ||
                  selectedSafe
                }
                onClick={approveSuggestion}
              >
                {busyId === selectedSuggestion.id
                  ? "Working..."
                  : selectedExact
                    ? "Approve + Add Applicability"
                    : "Add Variant + Applicability + Approve"}
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
